import './Layout.scss';
import React, { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { Stack } from '@mui/material';
import { NavLink, Outlet } from 'react-router-dom';

import home from '../../assets/images/home.svg';
import voyage from '../../assets/images/voyage.svg';
import User from '../../assets/images/user.svg';
import contacts from '../../assets/images/contacts.svg';
import client from '../../assets/images/client.svg';
import handshake from '../../assets/images/handshake.svg';
import settings from '../../assets/images/settings.svg';
import Logo from '../../assets/images/logo-round.svg';
import Portfolio from '../../assets/images/portfolio.svg';
import Header from '../../components/header/Header';
import TooltipBox from '../../components/TooltipBox';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { initNotifyCount } from '../../redux/features/layoutSlice';

const drawerWidth = 240;
const miniDrawerWidth = 56;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: miniDrawerWidth,
  [theme.breakpoints.up('sm')]: {
    width: miniDrawerWidth,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  // justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  marginLeft: 0,
  width: `calc(100% - ${miniDrawerWidth}px)`,
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const Brand = ({ expanded }) => {
  const theme = useTheme();
  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      className="!max-h-[56px] bg-white mt-[16px]"
    >
      <Stack
        alignItems={'center'}
        justifyContent={'center'}
        className="h-[56px] w-[56px]  "
      >
        <img
          className="w-[35px] h-[35px]"
          src={Logo}
        />
      </Stack>
      <Stack
        justifyContent={'center'}
        className={'!overflow-hidden  ' + (expanded ? 'expandedAnimation' : 'closedAnimation')}
      >
        <Typography className="ff-Roboto brandName transition-all">Emissions</Typography>
        <Typography className="ff-Roboto brandSlogan transition-all">Charting a course to carbon neutrality</Typography>
      </Stack>
    </Stack>
  );
};

const menuArr = [
  {
    id: 1,
    name: 'Dashboard',
    flag: true,
    icon: home,
    route: '',
    isRoute: true,
    moduleName: '',
  },
  {
    id: 2,
    name: 'Fleet Management',
    flag: true,
    icon: voyage,
    route: 'fleetManagement',
    isRoute: true,
    moduleName: 'fleetManagement',
  },
  {
    id: 3,
    name: 'Trade Management',
    flag: true,
    icon: handshake,
    route: 'tradeManagement',
    isRoute: true,
    moduleName: 'tradeManagement',
  },
  {
    id: 4,
    name: 'User Management',
    flag: true,
    icon: User,
    route: 'userManagement',
    isRoute: true,
    moduleName: 'userManagement',
  },
  {
    id: 5,
    name: 'Contact Management',
    flag: true,
    icon: contacts,
    route: 'contactManagement',
    isRoute: true,
    moduleName: 'contactManagement',
  },
  {
    id: 8,
    name: 'Client Management',
    flag: true,
    icon: client,
    route: 'clientManagement',
    isRoute: true,
    moduleName: 'clientManagement',
  },
  {
    id: 9,
    name: 'Portfolio Management',
    flag: true,
    icon: Portfolio,
    route: 'portfolioManagement',
    isRoute: true,
    moduleName: 'portfolioManagement',
  },
];

export default function Layout() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sideMenuList, setSideMenuList] = useState(menuArr);
  const [title, setTitle] = useState('');
  const { user } = useSelector((state) => state.auth);

  const dispatch = useDispatch();

  useEffect(() => {
    let arr = [...menuArr];
    setSideMenuList(arr);
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    let interval = setInterval(() => {
      dispatch(initNotifyCount());
    }, 1000 * 60)
    return () => {
      clearInterval(interval);
    }
  }, [])

  const handleDrawer = () => {
    setOpen(open ? false : true);
  };

  const checkMenuHasPermission = (menu) => {
    if(menu === 'Dashboard' && user?.type != 'trader') {
      return true;
    }
    if(menu === 'Fleet Management' && user?.type != 'trader') {
      return true;
    }
    if(menu === 'Trade Management') {
      return true;
    }
    if(menu === 'User Management') {
      return true;
    }
    if(menu === 'Contact Management' && user?.type != 'trader') {
      return true;
    }
    if(menu === 'Client Management' && user?.type === 'trader') {
      return true;
    }
    if(menu === 'Portfolio Management' && user?.type != 'trader') {
      return true;
    }
    return false;
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', backgroundColor: '#F2F2F3' }}>
      <CssBaseline />
      <AppBar
        sx={{ boxShadow: 'none' }}
        position="fixed"
        open={open}
      >
        <Header title={title} />
      </AppBar>
      <Drawer
        sx={{
          '& .MuiDrawer-paper': {
            background: theme.palette.primary.main,
            borderRight: 'none',
          },
        }}
        variant="permanent"
        open={open}
      >
        <DrawerHeader>
          <IconButton
            sx={{ color: theme.palette.white.main }}
            onClick={handleDrawer}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            className="fw-medium ff-Roboto !ml-[16px]"
            color={theme.palette.white.main}
          >
            {t('CarbonWallet')}
          </Typography>
        </DrawerHeader>
        <Stack flexGrow={1}>
          {sideMenuList?.map((menu, index) => {
            return menu.flag && checkMenuHasPermission(menu?.name) ? (
              menu.isRoute === true ? (
                menu?.name === 'Dashboard' ? (
                  <NavLink
                    key={index}
                    className={({ isActive }) => (isActive ? 'activeMenu' : 'inActiveMenu') + ' flex-aligncenter linkStyle ' + (menu.name === 'Settings' ? 'settingStyle' : '')}
                    style={{ textDecoration: 'none' }}
                    to={`/${menu.route}`}
                    onClick={() => {
                      setTitle(t(`${menu.name}`));
                    }}
                  >
                    <TooltipBox
                      key={index}
                      title={!open && t(menu.name)}
                      placement="right"
                    >
                      <div className="menuStyle">
                        <img
                          src={menu.icon}
                          alt={menu.name}
                        />
                      </div>
                    </TooltipBox>
                    {<Typography className={'menuName fs-12 fw-medium ff-Roboto ' + (open ? 'expandedAnimation' : 'closedAnimation')}>{t(`${menu.name}`)}</Typography>}
                  </NavLink>
                ) : (
                  <NavLink
                    key={index}
                    className={({ isActive }) => (isActive ? 'activeMenu' : 'inActiveMenu') + ' flex-aligncenter linkStyle ' + (menu.name === 'Settings' ? 'settingStyle' : '')}
                    style={{ textDecoration: 'none' }}
                    to={`/${menu.route}`}
                    onClick={() => {
                      setTitle(t(`${menu.name}`));
                    }}
                  >
                    <TooltipBox
                      key={index}
                      title={!open && t(`${menu.name}`)}
                      placement="right"
                    >
                      <div className="menuStyle">
                        <img
                          src={menu.icon}
                          alt={menu.name}
                        />
                      </div>
                    </TooltipBox>
                    {<Typography className={'menuName fs-12 fw-medium ff-Roboto ' + (open ? 'expandedAnimation' : 'closedAnimation')}>{t(`${menu.name}`)}</Typography>}
                  </NavLink>
                )
              ) : null
            ) : null;
          })}
        </Stack>
        <Stack>
          <NavLink
            className={({ isActive }) => (isActive ? 'activeMenu' : 'inActiveMenu') + ' flex-aligncenter linkStyle '}
            style={{ textDecoration: 'none' }}
            to={`/settings`}
            onClick={() => {
              // setTitle(menu?.name);
            }}
          >
            <TooltipBox
              key={6}
              title={!open && t('Settings')}
              placement="right"
            >
              <div className="menuStyle">
                <img
                  src={settings}
                  alt={'Settings'}
                />
              </div>
            </TooltipBox>
            {<Typography className={'menuName fs-12 fw-medium ff-Roboto ' + (open ? 'expandedAnimation' : 'closedAnimation')}>{t('Settings')}</Typography>}
          </NavLink>
          <Brand expanded={open} />
        </Stack>
      </Drawer>
      <Box
        component="main"
        sx={{ flexGrow: 1, height: '100%' }}
      >
        <Stack sx={{ height: '100%' }}>
          <Stack sx={{ height: miniDrawerWidth }} />
          <Outlet />
        </Stack>
      </Box>
    </Box>
  );
}
